import React from "react";
import { formatName, get_t_str_shortened } from "../../../../../helpers/helpers";
import { formatDateReadableWithDayOfWeekNoYear } from "../../../requests/utilities/helpers";
import { roleColorHex, shiftRoleId } from "./roleColors";
import {
  TONES,
  shiftTone,
  StripeLegend,
  getBreakLines,
  shiftTimeLabel,
} from "./WeeklyScheduleSheet";

export const GROUP_MODES = [
  { value: "role", label: "Role" },
  { value: "department", label: "Department" },
  { value: "none", label: "None" },
];

function accentFor(shift, colorMode, roleNames) {
  if (colorMode === "time") return TONES[shiftTone(shift)].color;
  if (colorMode === "role") return roleColorHex(shiftRoleId(shift), roleNames);
  return null;
}

function roleInfo(shift, roleNames) {
  return roleNames?.find((r) => r.id === shiftRoleId(shift));
}

/**
 * One printable day: a roster sorted by start time, optionally grouped, with
 * an optional timeline bar per shift.
 *
 * props
 *  title / subtitle   store name and the date
 *  shifts             assigned shifts for the day (already role-filtered)
 *  openShifts         unassigned shifts for the day
 *  roleNames          role catalogue (name, color, role_type)
 *  notes              that day's notes
 *  options { groupBy ("role"|"department"|"none"), colorMode, showTimeline,
 *            showStations, showBreaks, showHours, showOpenShifts, showNotes }
 */
export default function DailyScheduleSheet({
  title,
  subtitle,
  shifts = [],
  openShifts = [],
  roleNames,
  notes,
  options,
}) {
  const {
    groupBy = "role",
    colorMode = "time",
    showTimeline = true,
    showStations = true,
    showBreaks = false,
    showHours = true,
    showOpenShifts = true,
    showNotes = true,
  } = options || {};

  const byStart = (a, b) =>
    a.shift_start - b.shift_start ||
    a.shift_end - b.shift_end ||
    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);

  const sorted = [...shifts].sort(byStart);
  const open = showOpenShifts ? [...openShifts].sort(byStart) : [];
  const all = [...sorted, ...open];

  // Timeline bounds: whole hours around the earliest start / latest end.
  const dayStart = all.length ? Math.floor(Math.min(...all.map((s) => s.shift_start))) : 6;
  const dayEnd = all.length
    ? Math.ceil(Math.max(...all.map((s) => s.shift_end)))
    : 22;
  const span = Math.max(dayEnd - dayStart, 1);
  const pct = (h) => `${(Math.min(Math.max(h - dayStart, 0), span) / span) * 100}%`;
  const hourTicks = [];
  const tickStep = span > 14 ? 3 : 2;
  for (let h = dayStart; h <= dayEnd; h += tickStep) hourTicks.push(h);

  // Grouping
  const groups = [];
  if (groupBy === "none") {
    groups.push({ key: "all", label: null, shifts: sorted });
  } else {
    const map = new Map();
    sorted.forEach((s) => {
      const r = roleInfo(s, roleNames);
      const label =
        groupBy === "department"
          ? r?.role_type || "Other"
          : r?.role_name || s.role_name || "Other";
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(s);
    });
    [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([label, list]) => groups.push({ key: label, label, shifts: list }));
  }
  if (open.length) groups.push({ key: "__open", label: "Open shifts", shifts: open, isOpen: true });

  let legend = null;
  if (colorMode === "time") legend = Object.values(TONES);
  if (colorMode === "role") {
    const ids = new Set(all.map(shiftRoleId));
    legend = (roleNames || [])
      .filter((r) => ids.has(r.id))
      .map((r) => ({ label: r.role_name, color: roleColorHex(r.id, roleNames) }));
  }

  const totalHours = all.reduce((sum, s) => sum + (s.hours || 0), 0);
  const showRoleInline = groupBy !== "role";
  const colCount =
    1 + 1 + (showStations ? 1 : 0) + (showTimeline ? 1 : 0) + (showHours ? 1 : 0);

  const cell = "align-middle py-1 px-1.5";

  return (
    <div>
      <div className="flex items-end justify-between pb-2 mb-1 border-b-2 border-slate-900">
        <div>
          <h3 className="text-base font-semibold leading-tight">{title}</h3>
          {subtitle && (
            <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{subtitle}</p>
          )}
        </div>
        <StripeLegend items={legend} />
      </div>

      <table className="w-full table-fixed border-collapse text-[10px] leading-tight">
        <colgroup>
          <col style={{ width: 170 }} />
          <col style={{ width: 110 }} />
          {showStations && <col style={{ width: 90 }} />}
          {showTimeline && <col />}
          {showHours && <col style={{ width: 46 }} />}
        </colgroup>
        <thead>
          <tr className="border-b border-slate-400 text-[9px] uppercase tracking-wide text-slate-500">
            <th className="text-left font-semibold py-1.5 px-1.5 pl-0">Team member</th>
            <th className="text-left font-semibold py-1.5 px-1.5">Shift</th>
            {showStations && <th className="text-left font-semibold py-1.5 px-1.5">Station</th>}
            {showTimeline && (
              <th className="font-normal py-1 px-1.5">
                <div className="relative h-3">
                  {hourTicks.map((h) => (
                    <span
                      key={h}
                      className="absolute -translate-x-1/2 text-[8px] text-slate-400 normal-case tracking-normal"
                      style={{ left: pct(h) }}
                    >
                      {get_t_str_shortened(h)}
                    </span>
                  ))}
                </div>
              </th>
            )}
            {showHours && <th className="text-right font-semibold py-1.5 px-1.5">Hrs</th>}
          </tr>
        </thead>
        <tbody>
          {all.length === 0 && (
            <tr>
              <td colSpan={colCount} className="py-6 text-center text-slate-400 italic">
                No shifts scheduled for this day.
              </td>
            </tr>
          )}
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              {g.label && (
                <tr className="print-avoid-break">
                  <td
                    colSpan={colCount}
                    className="pt-2.5 pb-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300"
                  >
                    {g.label}
                    <span className="ml-1.5 font-normal normal-case tracking-normal text-slate-400">
                      {g.shifts.length}
                    </span>
                  </td>
                </tr>
              )}
              {g.shifts.map((s, i) => {
                const accent = accentFor(s, colorMode, roleNames);
                const r = roleInfo(s, roleNames);
                const name = g.isOpen
                  ? "Open"
                  : formatName(`${s.first_name || ""} ${s.last_name || ""}`);
                return (
                  <tr key={s.id ?? i} className="border-b border-slate-200">
                    <td className={`${cell} pl-0`}>
                      <div
                        className={`print-accent ${accent ? "pl-1.5" : ""}`}
                        style={accent ? { borderLeft: `3px solid ${accent}` } : undefined}
                      >
                        <p className={`font-semibold text-[10.5px] ${g.isOpen ? "italic text-slate-500" : ""}`}>
                          {name}
                        </p>
                        {(showRoleInline || g.isOpen) && (r?.role_name || s.role_name) && (
                          <p className="text-slate-500 text-[9px]">{r?.role_name || s.role_name}</p>
                        )}
                      </div>
                    </td>
                    <td className={`${cell} whitespace-nowrap`}>
                      <p className="font-semibold">{shiftTimeLabel(s)}</p>
                      {showBreaks &&
                        getBreakLines(s).map((l, j) => (
                          <p key={j} className="text-slate-400 italic text-[9px]">
                            {l}
                          </p>
                        ))}
                    </td>
                    {showStations && (
                      <td className={`${cell} text-slate-600`}>{s.station?.name || ""}</td>
                    )}
                    {showTimeline && (
                      <td className={cell}>
                        <div className="relative h-[7px]">
                          {hourTicks.map((h) => (
                            <span
                              key={h}
                              className="absolute top-0 bottom-0 border-l border-slate-200"
                              style={{ left: pct(h) }}
                            />
                          ))}
                          <span
                            className="print-accent absolute top-0 bottom-0 rounded-sm"
                            style={{
                              left: pct(s.shift_start),
                              width: `calc(${pct(s.shift_end)} - ${pct(s.shift_start)})`,
                              backgroundColor: accent || "#334155",
                            }}
                          />
                        </div>
                      </td>
                    )}
                    {showHours && (
                      <td className={`${cell} text-right tabular-nums text-slate-700`}>
                        {(s.hours || 0).toFixed(2)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
        {showHours && all.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-slate-900 font-semibold">
              <td colSpan={colCount - 1} className="py-1.5 pr-1.5">
                Total hours
                <span className="ml-1.5 font-normal text-slate-500">
                  {sorted.length} scheduled{open.length ? `, ${open.length} open` : ""}
                </span>
              </td>
              <td className="py-1.5 px-1.5 text-right tabular-nums">{totalHours.toFixed(2)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      {showBreaks && (
        <p className="mt-1.5 text-[9px] text-slate-400">
          B = break · B2 = second break · PB = paid break · BD = business decline
        </p>
      )}

      {showNotes && notes?.length > 0 && (
        <div className="mt-5 print-avoid-break">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 pb-1 mb-1.5">
            Notes
          </p>
          {notes.map((note, i) => (
            <div key={i} className="text-[10px] leading-snug py-0.5 border-b border-slate-100 flex gap-2">
              <span className="shrink-0 w-[72px] text-slate-500">
                {note.start_time
                  ? `${get_t_str_shortened(note.start_time)}${
                      note.end_time ? `–${get_t_str_shortened(note.end_time)}` : ""
                    }`
                  : formatDateReadableWithDayOfWeekNoYear(note.date)}
              </span>
              <span className="min-w-0">
                <span className="font-semibold">{note.title}</span>
                {note.description && (
                  <span className="block text-slate-500">{note.description}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
